"use client";

import { getEsimCatalogPublicFiltersService } from "@/lib/redux/features/esim/callbackServices/getEsimCatalogPublicFiltersService";
import { useEffect, useState } from "react";

export type EsimGoodPriceEntry = {
  price: number;
  feeMakeUp?: number;
  calculationMethod?: string;
  operation?: string;
};

type GoodPriceMap = Record<string, EsimGoodPriceEntry>;

let cachedGoodPriceByCountry: GoodPriceMap | null = null;
let inflightRequest: Promise<GoodPriceMap> | null = null;

const fetchGoodPriceByCountry = async (): Promise<GoodPriceMap> => {
  const res = await getEsimCatalogPublicFiltersService({ goodPriceCountry: true });
  const list = Array.isArray(res?.goodPriceCountry) ? res.goodPriceCountry : [];
  const map: GoodPriceMap = {};
  for (const entry of list) {
    if (entry?.country) {
      map[entry.country] = {
        price: entry.price,
        feeMakeUp: entry.feeMakeUp,
        calculationMethod: entry.calculationMethod,
        operation: entry.operation,
      };
    }
  }
  return map;
};

/**
 * Giá tốt nhất theo từng quốc gia (`POST /esim/catalog/public/filters` với `goodPriceCountry: true`,
 * api_guide mục 3.5) — gọi RIÊNG, KHÔNG qua Redux thunk (xem docs/planBuild/plan_improve.md, mục 2):
 * dùng chung 1 action Redux với `useEsimCatalogFilters` sẽ đụng cờ `called` chung của
 * `esim_catalog_public_filters`, gây mất dữ liệu chéo giữa 2 hook nếu request nào fulfilled trước.
 * Cache module-level — chỉ gọi API 1 lần/tab dù nhiều card/component cùng dùng hook này.
 */
export const useEsimGoodPriceCountry = () => {
  const [goodPriceByCountry, setGoodPriceByCountry] = useState<GoodPriceMap | null>(
    cachedGoodPriceByCountry,
  );
  const [loading, setLoading] = useState(!cachedGoodPriceByCountry);

  useEffect(() => {
    if (cachedGoodPriceByCountry) return;
    let cancelled = false;
    if (!inflightRequest) {
      inflightRequest = fetchGoodPriceByCountry();
    }
    inflightRequest
      .then((map) => {
        cachedGoodPriceByCountry = map;
        if (!cancelled) setGoodPriceByCountry(map);
      })
      .catch(() => {
        if (!cancelled) setGoodPriceByCountry({});
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { goodPriceByCountry, loading };
};
